import { injectable } from 'inversify';
import flatten from 'lodash/flatten';

import { FormatService } from '@vk-hr-tek/core/format';
import { Calendar } from '@vk-hr-tek/core/calendar';

import { BellNotification, BellNotificationPayload } from '@app/gen/bell';
import { modules } from '@app/modules';

import { Notification, NotificationGroup } from '../types';
import { NOTIFIABLE_NICK_ICONS } from '../constants';

@injectable()
export class NotificationsMapper {
  constructor(
    private format: FormatService,
    private calendar: Calendar,
  ) {}

  private formatGroupLabel(date: Date): string {
    return new Intl.DateTimeFormat('ru-RU', {
      day: 'numeric',
      month: 'long',
    }).format(date);
  }

  private buildUrl(segments: (string | undefined)[], suffix = ''): string {
    const isComplete = segments.every((segment) => segment);

    return isComplete ? `/${segments.join('/')}${suffix}` : '';
  }

  private getCommunityUrl(
    payload?: BellNotificationPayload,
    suffix = '',
  ): string {
    return this.buildUrl(
      [modules.community.basename, payload?.community_id, payload?.slug],
      suffix,
    );
  }

  private getCommunityPostUrl(
    payload?: BellNotificationPayload,
    anchor = '',
  ): string {
    return this.buildUrl(
      [
        modules.community.basename,
        payload?.community_id,
        payload?.slug,
        'post',
        payload?.post_id,
      ],
      anchor,
    );
  }

  private getSurveyUrl(payload?: BellNotificationPayload): string {
    return this.buildUrl([modules.surveys.basename, payload?.survey_id]);
  }

  private getCommentAnchor(
    payload: BellNotificationPayload | undefined,
    prefix: 'mention' | 'comment',
  ): string {
    return payload?.comment_id ? `#${prefix}-${payload.comment_id}` : '';
  }

  private getNotificationUrl(notification: BellNotification): string {
    const {
      payload,
      action_nick: actionNick,
      notifiable_nick: notifiableNick,
    } = notification;

    switch (actionNick) {
      case 'community_post_delete':
      case 'community_moderation_post_delete':
        return this.getCommunityUrl(payload);

      case 'community_post_suggest':
        return this.getCommunityUrl(payload, '?authorship=moderation');

      case 'community_post_reject':
        return this.getCommunityUrl(payload, '?authorship=suggested');

      case 'community_post_publication':
      case 'community_new_post':
      case 'community_post_mention':
        return this.getCommunityPostUrl(payload);

      case 'community_comment_mention':
        return this.getCommunityPostUrl(
          payload,
          this.getCommentAnchor(payload, 'mention'),
        );

      case 'community_new_comment':
      case 'community_comment_reply':
        return this.getCommunityPostUrl(
          payload,
          this.getCommentAnchor(payload, 'comment'),
        );

      case 'community_application':
        return this.getCommunityUrl(
          payload,
          '/manage?filter_status=join_request',
        );

      case 'survey_start':
      case 'survey_remind':
      case 'survey_end':
        return this.getSurveyUrl(payload);
    }

    switch (notifiableNick) {
      case 'news':
      case 'comment':
        return this.buildUrl(
          [modules.news.basename, payload?.news_id],
          this.getCommentAnchor(payload, 'mention'),
        );

      case 'community':
        return this.getCommunityUrl(payload);

      case 'community_manage':
        return this.getCommunityUrl(payload, '/manage');

      case 'community_post':
      case 'community_post_comment':
        return this.buildUrl(
          [
            modules.community.basename,
            payload?.community_id,
            'post',
            payload?.post_id,
          ],
          this.getCommentAnchor(payload, 'mention'),
        );
    }

    return '';
  }

  processEntity(notification: BellNotification): Notification {
    return {
      id: notification.id,
      notifiableName: notification.notifiable_name,
      notifiableNick: notification.notifiable_nick,
      actionName: notification.action_name,
      actionNick: notification.action_nick,
      message: notification.message,
      createdAt: notification.created_at,
      isRead: !!notification.read_at,
      icon: NOTIFIABLE_NICK_ICONS[notification.notifiable_nick],
      url: this.getNotificationUrl(notification),
    };
  }

  processEntityListWithDateGroups(
    notifications: BellNotification[],
  ): NotificationGroup[] {
    const todayDate = new Date();
    const today = this.formatGroupLabel(todayDate);
    const yesterday = this.formatGroupLabel(this.calendar.sub(todayDate, 1));

    const groupedNotifications: Record<string, NotificationGroup[]> = {
      today: [{ label: `Сегодня, ${today}`, type: 'group' }],
      yesterday: [{ label: `Вчера, ${yesterday}`, type: 'group' }],
      thisWeek: [{ label: 'На этой неделе', type: 'group' }],
      thisMonth: [{ label: 'В этом месяце', type: 'group' }],
      older: [{ label: 'Более месяца назад', type: 'group' }],
    };

    notifications.forEach((notification) => {
      const mappedNotification = {
        ...this.processEntity(notification),
        type: 'item' as const,
      };
      const { createdAt } = mappedNotification;

      switch (true) {
        case this.calendar.isToday(createdAt):
          return groupedNotifications.today.push({
            ...mappedNotification,
            createdAt: this.format.toTime(createdAt),
          });
        case this.calendar.isYesterday(createdAt):
          return groupedNotifications.yesterday.push({
            ...mappedNotification,
            createdAt: this.format.toTime(createdAt),
          });
        case this.calendar.isThisWeek(createdAt, 1):
          return groupedNotifications.thisWeek.push({
            ...mappedNotification,
            createdAt: this.format.toDateTime(createdAt),
          });
        case this.calendar.isThisMonth(createdAt):
          return groupedNotifications.thisMonth.push({
            ...mappedNotification,
            createdAt: this.format.toDateTime(createdAt),
          });
        default:
          return groupedNotifications.older.push({
            ...mappedNotification,
            createdAt: this.format.toDate(createdAt),
          });
      }
    });

    return flatten(
      Object.values(groupedNotifications).filter(
        (groupArray) => groupArray.length > 1,
      ),
    );
  }
}
